import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Upload,
} from "antd";
import { ArrowLeftOutlined, DollarOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE,
  FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE,
  FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { addLogEntry } from "../../../../../utils/logging";
import { formatPriceUsd } from "../../../../../functions/formatters";
import urls from "../../../../../urls";
import { useSelector } from "react-redux";
import { usePurchaseOrderInvoices, usePurchaseOrderPayments } from "../../../../../data/hooks";

const { Panel } = Collapse;
const { TextArea } = Input;

const statusColors = {
  draft: "default",
  shipped: "blue",
  in_transit: "orange",
  received: "green",
  completed: "cyan",
  cancelled: "red",
};

const statusLabels = {
  draft: "Concept",
  shipped: "Verzonden",
  in_transit: "Onderweg",
  received: "Ontvangen",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderShipmentDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const [shipment, setShipment] = useState(null);
  const [shipmentInvoiceId, setShipmentInvoiceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [showGoodsReceiptModal, setShowGoodsReceiptModal] = useState(false);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [receiptDate, setReceiptDate] = useState(dayjs());
  const [processingReceipt, setProcessingReceipt] = useState(false);
  const [editingShipment, setEditingShipment] = useState(false);
  const [editedShipmentItems, setEditedShipmentItems] = useState([]);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [editedInvoiceLines, setEditedInvoiceLines] = useState([]);
  const [editedAdditionalCosts, setEditedAdditionalCosts] = useState([]);
  const email = useSelector((state) => state.firebase?.auth?.email || "");

  // Use hooks for invoices and payments
  const { invoices } = usePurchaseOrderInvoices({ shipmentId: id });
  const invoice = invoices.length > 0 ? invoices[0] : null;
  const { payments } = usePurchaseOrderPayments({ invoiceId: invoice?.id });

  const loadShipment = async () => {
    try {
      const doc = await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).get();
      if (doc.exists) {
        const shipmentData = { id: doc.id, ...doc.data() };
        setShipment(shipmentData);
        setShipmentInvoiceId(shipmentData.invoiceId || null);
      } else {
        message.error(t("shipment.not_found"));
      }
    } catch (error) {
      console.error("Error loading shipment:", error);
      message.error(t("shipment.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadShipment();
  }, [id]);

  const handleUpdateStatus = async (newStatus) => {
    try {
      setSaving(true);
      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "status_changed",
        user: email,
        oldValue: shipment.status,
        newValue: newStatus,
      });

      const updateData = {
        status: newStatus,
        log: newLog,
      };

      if (newStatus === "received") {
        updateData.receivedAt = dayjs().unix();
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update(updateData);
      message.success(t("shipment.updated_success"));
      await loadShipment();
    } catch (error) {
      console.error("Error updating shipment status:", error);
      message.error(t("shipment.update_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateInvoiceStatus = async (newStatus) => {
    if (!invoice) return;
    try {
      setSaving(true);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success("Factuurstatus bijgewerkt");
      await loadShipment();
    } catch (error) {
      console.error("Error updating invoice status:", error);
      message.error("Fout bij bijwerken factuurstatus");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditingShipment = () => {
    setEditedShipmentItems(JSON.parse(JSON.stringify(shipment.items || [])));
    setEditingShipment(true);
  };

  const handleCancelEditingShipment = () => {
    setEditingShipment(false);
    setEditedShipmentItems([]);
  };

  const handleShipmentItemChange = (index, field, value) => {
    const updated = [...editedShipmentItems];
    updated[index] = { ...updated[index], [field]: value };
    setEditedShipmentItems(updated);
  };

  const handleSaveShipmentEdits = async () => {
    try {
      setSaving(true);

      // Recalculate totals
      const totalProducts = editedShipmentItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const totalPrice = editedShipmentItems.reduce(
        (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
        0
      );

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({
        items: editedShipmentItems,
        totalProducts,
        totalPrice,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success("Verzending bijgewerkt");
      setEditingShipment(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving shipment edits:", error);
      message.error("Fout bij opslaan wijzigingen");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditingInvoice = () => {
    setEditedInvoiceLines(JSON.parse(JSON.stringify(invoice.lines || [])));
    setEditedAdditionalCosts(JSON.parse(JSON.stringify(invoice.additionalCosts || [])));
    setEditingInvoice(true);
  };

  const handleCancelEditingInvoice = () => {
    setEditingInvoice(false);
    setEditedInvoiceLines([]);
    setEditedAdditionalCosts([]);
  };

  const handleInvoiceLineChange = (index, field, value) => {
    const updated = [...editedInvoiceLines];
    const line = { ...updated[index] };
    
    if (field === 'quantity' || field === 'unitPrice' || field === 'discountPercentage') {
      line[field] = value;
      // Recalculate totalPrice for this line
      const qty = field === 'quantity' ? value : (line.quantity || 0);
      const price = field === 'unitPrice' ? value : (line.unitPrice || 0);
      const discount = field === 'discountPercentage' ? value : (line.discountPercentage || 0);
      line.totalPrice = qty * price * (1 - discount / 100);
    } else {
      line[field] = value;
    }
    
    updated[index] = line;
    setEditedInvoiceLines(updated);
  };

  const handleAdditionalCostChange = (index, field, value) => {
    const updated = [...editedAdditionalCosts];
    updated[index] = { ...updated[index], [field]: value };
    setEditedAdditionalCosts(updated);
  };

  const handleSaveInvoiceEdits = async () => {
    try {
      setSaving(true);

      // Recalculate totals
      const subtotalProducts = editedInvoiceLines.reduce((sum, line) => sum + (line.totalPrice || 0), 0);
      const subtotalAdditionalCosts = editedAdditionalCosts.reduce((sum, cost) => sum + (cost.amount || 0), 0);
      const totalAmount = subtotalProducts + subtotalAdditionalCosts;
      const currentTotalPaid = invoice.totalPaid || 0;
      const amountDue = totalAmount - currentTotalPaid;

      // Determine status based on payment
      let status = invoice.status;
      if (currentTotalPaid >= totalAmount) {
        status = "paid";
      } else if (currentTotalPaid > 0) {
        status = "partially_paid";
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        lines: editedInvoiceLines,
        additionalCosts: editedAdditionalCosts.length > 0 ? editedAdditionalCosts : null,
        subtotalProducts,
        subtotalAdditionalCosts,
        totalAmount,
        amountDue: amountDue > 0 ? amountDue : 0,
        status,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success("Factuur bijgewerkt");
      setEditingInvoice(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving invoice edits:", error);
      message.error("Fout bij opslaan wijzigingen");
    } finally {
      setSaving(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount(null);
    setPaymentDate(dayjs());
    setPaymentReference("");
    setPaymentNotes("");
    setPaymentProofFile(null);
  };

  const handleOpenGoodsReceiptModal = () => {
    // Initialize received quantities with shipped quantities
    const initialQuantities = {};
    (shipment.items || []).forEach((item, index) => {
      initialQuantities[index] = item.quantity;
    });
    setReceivedQuantities(initialQuantities);
    setReceiptDate(dayjs());
    setShowGoodsReceiptModal(true);
  };

  const handleProcessGoodsReceipt = async () => {
    try {
      setProcessingReceipt(true);

      // Build lines for API
      const lines = (shipment.items || []).map((item, index) => ({
        itemCode: item.productCode,
        orderedAmount: item.quantity,
        receivedAmount: receivedQuantities[index] || 0,
      }));

      // Get first purchase order number for API call
      const firstOrderNumber = shipment.sourceOrders?.[0]?.purchaseOrderNumber;
      if (!firstOrderNumber) {
        message.error("Geen inkooporder nummer gevonden");
        return;
      }

      // Call API to process goods receipt
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/exact/purchase-orders/${firstOrderNumber}/completed`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            receiptDate: receiptDate.format("DD-MM-YYYY"),
            lines,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "API fout");
      }

      // Update shipment status to completed and add received quantities
      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "goods_receipt_processed",
        user: email,
        receiptDate: receiptDate.unix(),
      });

      await firestore
        .collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE)
        .doc(id)
        .update({
          status: "completed",
          receivedAt: receiptDate.unix(),
          receivedQuantities: lines.map((l, idx) => ({
            productCode: l.itemCode,
            orderedAmount: l.orderedAmount,
            receivedAmount: l.receivedAmount,
          })),
          log: newLog,
        });

      message.success("Ontvangst verwerkt! Voorraad bijgewerkt in Exact en boekhouder is geïnformeerd.");
      setShowGoodsReceiptModal(false);
      await loadShipment();
    } catch (error) {
      console.error("Error processing goods receipt:", error);
      message.error(`Fout bij verwerken ontvangst: ${error.message}`);
    } finally {
      setProcessingReceipt(false);
    }
  };

  const handleAddPayment = async () => {
    if (!invoice || !paymentAmount || paymentAmount <= 0) {
      message.error("Vul een geldig bedrag in");
      return;
    }
    if (!paymentDate) {
      message.error("Selecteer een betaaldatum");
      return;
    }

    try {
      setSavingPayment(true);

      let proofUrl = null;
      if (paymentProofFile) {
        const storage = getStorage();
        const fileName = `payments/${invoice.id}/${Date.now()}_${paymentProofFile.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, paymentProofFile);
        proofUrl = await getDownloadURL(storageRef);
      }

      const paymentData = {
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        shipmentId: shipment.id,
        supplierId: shipment.supplierId,
        supplierName: shipment.supplierName || null,
        amount: paymentAmount,
        currency: "USD",
        paymentDate: paymentDate.unix(),
        reference: paymentReference.trim() || null,
        notes: paymentNotes.trim() || null,
        proofUrl,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).add(paymentData);

      // Update invoice totals
      const newTotalPaid = (invoice.totalPaid || 0) + paymentAmount;
      const newAmountDue = (invoice.totalAmount || 0) - newTotalPaid;
      const newStatus =
        newTotalPaid >= (invoice.totalAmount || 0)
          ? "paid"
          : newTotalPaid > 0
            ? "partially_paid"
            : invoice.status || "submitted";

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success("Betaling toegevoegd");
      setShowPaymentModal(false);
      resetPaymentForm();
      await loadShipment();
    } catch (error) {
      console.error("Error adding payment:", error);
      message.error("Fout bij toevoegen betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!shipment) return null;

  // Group items by source order
  const itemsByOrder = (shipment.items || []).reduce((acc, item) => {
    const key = item.purchaseOrderId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.purchaseOrderShipmentsList)}>
                {t("back")}
              </Button>
              <span>{shipment.shipmentNumber}</span>
              <Tag color={statusColors[shipment.status]}>{statusLabels[shipment.status]}</Tag>
            </Space>
            <Space>
              {shipment.status === "received" && (
                <Button type="primary" onClick={handleOpenGoodsReceiptModal}>
                  Verwerk ontvangst
                </Button>
              )}
              <Select
                value={shipment.status}
                style={{ minWidth: 180 }}
                onChange={handleUpdateStatus}
                loading={saving}
                options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
              />
            </Space>
          </div>
        }
      >
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label={t("shipment.shipment") + " #"}>{shipment.shipmentNumber}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusColors[shipment.status]}>{statusLabels[shipment.status]}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("supplier.supplier")}>{shipment.supplierName || "-"}</Descriptions.Item>
          <Descriptions.Item label={t("total_products")}>{shipment.totalProducts}</Descriptions.Item>
          <Descriptions.Item label={t("total_price")}>{formatPriceUsd(shipment.totalPrice || 0)}</Descriptions.Item>
          <Descriptions.Item label={t("tracking_code")}>
            {shipment.trackingCode ? (
              <a
                href={shipment.trackingCode.startsWith("http") ? shipment.trackingCode : undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {shipment.trackingCode}
              </a>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          {shipment.cubicMeters && (
            <Descriptions.Item label={t("cubic_meters")}>{shipment.cubicMeters} m³</Descriptions.Item>
          )}
          {shipment.etd && (
            <Descriptions.Item label={t("etd")}>{dayjs.unix(shipment.etd).format("DD-MM-YYYY")}</Descriptions.Item>
          )}
          {shipment.shippedAt && (
            <Descriptions.Item label="Verzonden op">
              {dayjs.unix(shipment.shippedAt).format("DD-MM-YYYY HH:mm")}
            </Descriptions.Item>
          )}
          {shipment.receivedAt && (
            <Descriptions.Item label={t("received_at")}>
              {dayjs.unix(shipment.receivedAt).format("DD-MM-YYYY HH:mm")}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t("created_by")}>{shipment.createdBy}</Descriptions.Item>
          <Descriptions.Item label={t("date")}>
            {dayjs.unix(shipment.createdAt).format("DD-MM-YYYY HH:mm")}
          </Descriptions.Item>
        </Descriptions>

        <h4 style={{ marginTop: 24 }}>{t("order.purchase_orders")}</h4>
        <Space wrap>
          {shipment.sourceOrders?.map((so) => (
            <Tag
              key={so.purchaseOrderId}
              color="blue"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`${urls.purchaseOrdersList}/${so.purchaseOrderId}`)}
            >
              {so.purchaseOrderNumber}
            </Tag>
          ))}
        </Space>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
          <h4 style={{ margin: 0 }}>{t("products")}</h4>
          {!editingShipment && (
            <Button size="small" onClick={handleStartEditingShipment}>
              Aanpassen
            </Button>
          )}
          {editingShipment && (
            <Space>
              <Button size="small" onClick={handleCancelEditingShipment}>
                Annuleren
              </Button>
              <Button size="small" type="primary" onClick={handleSaveShipmentEdits} loading={saving}>
                Opslaan
              </Button>
            </Space>
          )}
        </div>
        
        <Table
          dataSource={editingShipment ? editedShipmentItems.map((item, idx) => ({ ...item, key: idx, _index: idx })) : (shipment.items || []).map((item, idx) => ({ ...item, key: idx }))}
          pagination={false}
          size="small"
          style={{ marginTop: 12 }}
          columns={[
            {
              title: t("product"),
              dataIndex: "productName",
              render: (name, record) => (
                <span>
                  {name}
                  {record.productColor && (
                    <span style={{ color: "#666", marginLeft: 4 }}>({record.productColor})</span>
                  )}
                </span>
              ),
            },
            { title: "Code", dataIndex: "productCode", render: (v) => v || "-" },
            {
              title: t("quantity"),
              dataIndex: "quantity",
              render: (value, record) =>
                editingShipment ? (
                  <InputNumber
                    min={0}
                    value={value}
                    onChange={(v) => handleShipmentItemChange(record._index, "quantity", v || 0)}
                    style={{ width: 80 }}
                  />
                ) : (
                  value
                ),
            },
            {
              title: t("unit_price"),
              dataIndex: "unitPrice",
              render: (value, record) =>
                editingShipment ? (
                  <InputNumber
                    min={0}
                    step={0.01}
                    prefix="$"
                    value={value}
                    onChange={(v) => handleShipmentItemChange(record._index, "unitPrice", v || 0)}
                    style={{ width: 100 }}
                  />
                ) : (
                  formatPriceUsd(value || 0)
                ),
            },
            {
              title: t("total"),
              render: (_, record) => formatPriceUsd((record.quantity || 0) * (record.unitPrice || 0)),
            },
          ]}
        />

        {/* Invoice */}
        {invoice && (
          <Card
            size="small"
            style={{ marginTop: 24, backgroundColor: "#f6ffed", borderColor: "#b7eb8f" }}
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#389e0d", fontWeight: "bold" }}>📋 Factuur — {invoice.invoiceNumber}</span>
                <Space>
                  <Tag color={invoiceStatusColors[invoice.status] || "default"}>
                    {invoiceStatusLabels[invoice.status] || invoice.status || "Ingediend"}
                  </Tag>
                  <Select
                    size="small"
                    value={invoice.status || "submitted"}
                    style={{ minWidth: 140 }}
                    onChange={handleUpdateInvoiceStatus}
                    loading={saving}
                    options={Object.entries(invoiceStatusLabels).map(([value, label]) => ({ value, label }))}
                  />
                </Space>
              </div>
            }
          >
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Factuurnummer">{invoice.invoiceNumber}</Descriptions.Item>
              <Descriptions.Item label="Factuurdatum">
                {invoice.invoiceDate ? dayjs.unix(invoice.invoiceDate).format("DD-MM-YYYY") : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Subtotaal producten">
                {formatPriceUsd(invoice.subtotalProducts || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Bijkomende kosten">
                {formatPriceUsd(invoice.subtotalAdditionalCosts || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Totaalbedrag">
                <span style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>
                  {formatPriceUsd(invoice.totalAmount || 0)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Betaald / Openstaand">
                <span style={{ fontWeight: "bold", color: "#1890ff" }}>{formatPriceUsd(invoice.totalPaid || 0)}</span>
                {" / "}
                <span style={{ fontWeight: "bold", color: (invoice.amountDue || 0) > 0 ? "#ff4d4f" : "#52c41a" }}>
                  {formatPriceUsd(invoice.amountDue ?? invoice.totalAmount ?? 0)}
                </span>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
              <h5 style={{ margin: 0 }}>Factuurregels</h5>
              {!editingInvoice && (
                <Button size="small" onClick={handleStartEditingInvoice}>
                  Factuur aanpassen
                </Button>
              )}
              {editingInvoice && (
                <Space>
                  <Button size="small" onClick={handleCancelEditingInvoice}>
                    Annuleren
                  </Button>
                  <Button size="small" type="primary" onClick={handleSaveInvoiceEdits} loading={saving}>
                    Opslaan
                  </Button>
                </Space>
              )}
            </div>

            {(editingInvoice ? editedInvoiceLines : invoice.lines || []).length > 0 && (
              <Table
                dataSource={(editingInvoice ? editedInvoiceLines : invoice.lines || []).map((l, i) => ({ ...l, key: i, _index: i }))}
                pagination={false}
                size="small"
                style={{ marginTop: 12, marginBottom: 16 }}
                columns={[
                  {
                    title: t("product"),
                    dataIndex: "productName",
                    render: (name, record) => (
                      <span>
                        {name}
                        {record.productColor && (
                          <span style={{ color: "#666", marginLeft: 4 }}>({record.productColor})</span>
                        )}
                      </span>
                    ),
                  },
                  {
                    title: t("quantity"),
                    dataIndex: "quantity",
                    render: (value, record) =>
                      editingInvoice ? (
                        <InputNumber
                          min={0}
                          value={value}
                          onChange={(v) => handleInvoiceLineChange(record._index, "quantity", v || 0)}
                          style={{ width: 80 }}
                        />
                      ) : (
                        value
                      ),
                  },
                  {
                    title: t("unit_price"),
                    dataIndex: "unitPrice",
                    render: (value, record) =>
                      editingInvoice ? (
                        <InputNumber
                          min={0}
                          step={0.01}
                          prefix="$"
                          value={value}
                          onChange={(v) => handleInvoiceLineChange(record._index, "unitPrice", v || 0)}
                          style={{ width: 100 }}
                        />
                      ) : (
                        formatPriceUsd(value || 0)
                      ),
                  },
                  {
                    title: "Korting",
                    dataIndex: "discountPercentage",
                    render: (value, record) =>
                      editingInvoice ? (
                        <InputNumber
                          min={0}
                          max={100}
                          value={value}
                          onChange={(v) => handleInvoiceLineChange(record._index, "discountPercentage", v || 0)}
                          style={{ width: 70 }}
                          suffix="%"
                        />
                      ) : (
                        value ? `${value}%` : "-"
                      ),
                  },
                  { title: t("total"), dataIndex: "totalPrice", render: (v) => formatPriceUsd(v || 0) },
                ]}
              />
            )}

            {(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).length > 0 && (
              <>
                <h5 style={{ marginTop: 16 }}>Bijkomende kosten</h5>
                <Table
                  dataSource={(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).map((c, i) => ({ ...c, key: i, _index: i }))}
                  pagination={false}
                  size="small"
                  style={{ marginBottom: 16 }}
                  columns={[
                    { title: "Omschrijving", dataIndex: "description" },
                    { title: t("quantity"), dataIndex: "quantity", render: (v) => v || 1 },
                    { title: t("unit_price"), dataIndex: "unitPrice", render: (v) => (v ? formatPriceUsd(v) : "-") },
                    {
                      title: t("total"),
                      dataIndex: "amount",
                      render: (value, record) =>
                        editingInvoice ? (
                          <InputNumber
                            min={0}
                            step={0.01}
                            prefix="$"
                            value={value}
                            onChange={(v) => handleAdditionalCostChange(record._index, "amount", v || 0)}
                            style={{ width: 100 }}
                          />
                        ) : (
                          formatPriceUsd(value || 0)
                        ),
                    },
                  ]}
                />
              </>
            )}

            {invoice.pdfUrl && (
              <div style={{ marginTop: 8 }}>
                <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer">
                  <Button type="primary" size="small">
                    Download factuur PDF
                  </Button>
                </a>
              </div>
            )}

            {invoice.remarks && (
              <div style={{ marginTop: 12, padding: 8, backgroundColor: "#fff", borderRadius: 4 }}>
                <strong>Opmerkingen:</strong>
                <div style={{ marginTop: 4, color: "#666" }}>{invoice.remarks}</div>
              </div>
            )}

            <Space style={{ marginTop: 12 }}>
              <Tag color="green">✓ Factuur ontvangen</Tag>
              {invoice.exactPurchaseInvoiceId && <Tag color="blue">✓ Gesynchroniseerd met Exact</Tag>}
            </Space>

            {/* Payments Section */}
            <div style={{ marginTop: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h5 style={{ margin: 0 }}>💰 Betalingen</h5>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={() => {
                    resetPaymentForm();
                    setPaymentAmount(invoice.amountDue > 0 ? invoice.amountDue : null);
                    setShowPaymentModal(true);
                  }}
                >
                  Betaling toevoegen
                </Button>
              </div>

              {payments.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    backgroundColor: "#fafafa",
                    borderRadius: 8,
                    textAlign: "center",
                    color: "#999",
                  }}
                >
                  Nog geen betalingen geregistreerd
                </div>
              ) : (
                <Table
                  dataSource={payments.map((p, i) => ({ ...p, key: i }))}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: "Datum",
                      dataIndex: "paymentDate",
                      render: (v) => (v ? dayjs.unix(v).format("DD-MM-YYYY") : "-"),
                    },
                    {
                      title: "Bedrag",
                      dataIndex: "amount",
                      render: (v) => <strong style={{ color: "#52c41a" }}>{formatPriceUsd(v || 0)}</strong>,
                    },
                    {
                      title: "Referentie",
                      dataIndex: "reference",
                      render: (v) => v || "-",
                    },
                    {
                      title: "Notities",
                      dataIndex: "notes",
                      render: (v) => v || "-",
                    },
                    {
                      title: "Bewijs",
                      dataIndex: "proofUrl",
                      render: (v) =>
                        v ? (
                          <a href={v} target="_blank" rel="noopener noreferrer">
                            <Button size="small" type="link" style={{ padding: 0 }}>
                              Bekijk
                            </Button>
                          </a>
                        ) : (
                          "-"
                        ),
                    },
                    {
                      title: "Door",
                      dataIndex: "createdBy",
                      render: (v) => <span style={{ fontSize: 11, color: "#888" }}>{v}</span>,
                    },
                  ]}
                />
              )}
            </div>
          </Card>
        )}

        {/* Goods Receipt Modal */}
        <Modal
          title="📦 Verwerk Ontvangst - Tellingen Invoeren"
          open={showGoodsReceiptModal}
          onOk={handleProcessGoodsReceipt}
          onCancel={() => setShowGoodsReceiptModal(false)}
          okText="Ontvangst verwerken"
          cancelText="Annuleren"
          confirmLoading={processingReceipt}
          width={800}
        >
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
              <p style={{ margin: 0, fontWeight: 500 }}>ℹ️ Vul hieronder de getelde aantallen in per product.</p>
              <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#666" }}>
                Na verwerking wordt de voorraad automatisch bijgewerkt in Exact Online en ontvangt de boekhouder een
                email.
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Ontvangstdatum <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <DatePicker style={{ width: "100%" }} value={receiptDate} onChange={setReceiptDate} format="DD-MM-YYYY" />
            </div>

            <h5 style={{ marginBottom: 12 }}>Producten</h5>
            <Table
              dataSource={(shipment.items || []).map((item, index) => ({ ...item, index, key: index }))}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Product",
                  dataIndex: "productName",
                  render: (name, record) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{name}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>Code: {record.productCode || "-"}</div>
                    </div>
                  ),
                },
                {
                  title: "Verzonden",
                  dataIndex: "quantity",
                  width: 100,
                  render: (qty) => <span style={{ fontWeight: 500 }}>{qty}</span>,
                },
                {
                  title: "Geteld Aantal",
                  dataIndex: "index",
                  width: 150,
                  render: (index, record) => (
                    <InputNumber
                      min={0}
                      value={receivedQuantities[index]}
                      onChange={(value) => setReceivedQuantities({ ...receivedQuantities, [index]: value })}
                      style={{ width: "100%" }}
                      status={receivedQuantities[index] !== record.quantity ? "warning" : undefined}
                    />
                  ),
                },
                {
                  title: "Verschil",
                  dataIndex: "index",
                  width: 100,
                  render: (index, record) => {
                    const diff = (receivedQuantities[index] || 0) - record.quantity;
                    return (
                      <span
                        style={{ color: diff === 0 ? "#52c41a" : diff < 0 ? "#ff4d4f" : "#faad14", fontWeight: 500 }}
                      >
                        {diff > 0 ? "+" : ""}
                        {diff}
                      </span>
                    );
                  },
                },
              ]}
            />

            <div style={{ marginTop: 16, padding: 12, backgroundColor: "#fff7e6", borderRadius: 8 }}>
              <p style={{ margin: 0, fontSize: 12, color: "#d48806" }}>
                ⚠️ <strong>Let op:</strong> Deze actie kan niet ongedaan worden gemaakt. De voorraad wordt direct
                bijgewerkt in Exact Online.
              </p>
            </div>
          </div>
        </Modal>

        {/* Payment Modal */}
        <Modal
          title="Betaling toevoegen"
          open={showPaymentModal}
          onOk={handleAddPayment}
          onCancel={() => {
            setShowPaymentModal(false);
            resetPaymentForm();
          }}
          okText="Betaling opslaan"
          confirmLoading={savingPayment}
        >
          <div style={{ marginTop: 16 }}>
            {invoice && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
                <div>
                  Factuur: <strong>{invoice.invoiceNumber}</strong>
                </div>
                <div>
                  Totaal: <strong>{formatPriceUsd(invoice.totalAmount || 0)}</strong>
                </div>
                <div>
                  Reeds betaald: <strong>{formatPriceUsd(invoice.totalPaid || 0)}</strong>
                </div>
                <div>
                  Openstaand:{" "}
                  <strong style={{ color: "#ff4d4f" }}>
                    {formatPriceUsd(invoice.amountDue ?? invoice.totalAmount ?? 0)}
                  </strong>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Bedrag (USD) <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                step={0.01}
                precision={2}
                value={paymentAmount}
                onChange={setPaymentAmount}
                placeholder="0.00"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Betaaldatum <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <DatePicker style={{ width: "100%" }} value={paymentDate} onChange={setPaymentDate} format="DD-MM-YYYY" />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Referentie / Transactie ID</label>
              <Input
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="bijv. TXN-123456"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Notities</label>
              <TextArea
                rows={2}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Optionele opmerkingen"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Betalingsbewijs (PDF/afbeelding)
              </label>
              <Upload
                accept=".pdf,.jpg,.jpeg,.png"
                maxCount={1}
                beforeUpload={(file) => {
                  if (file.size > 10 * 1024 * 1024) {
                    message.error("Bestand moet kleiner zijn dan 10MB");
                    return Upload.LIST_IGNORE;
                  }
                  setPaymentProofFile(file);
                  return false;
                }}
                onRemove={() => setPaymentProofFile(null)}
                fileList={
                  paymentProofFile
                    ? [{ uid: "-1", name: paymentProofFile.name, status: "done", size: paymentProofFile.size }]
                    : []
                }
              >
                <Button icon={<UploadOutlined />}>Selecteer bestand</Button>
              </Upload>
            </div>
          </div>
        </Modal>

        {/* Activity Log */}
        {shipment.log && shipment.log.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Collapse>
              <Panel header={t("activity_log")} key="1">
                <div style={{ maxHeight: 400, overflowY: "auto", paddingTop: 20 }}>
                  <Timeline>
                    {[...shipment.log].reverse().map((entry, idx) => (
                      <Timeline.Item key={idx}>
                        <div>
                          <p style={{ margin: 0 }}>
                            <strong>{entry.action}</strong>
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
                            {entry.user} - {dayjs.unix(entry.timestamp).format("DD-MM-YYYY HH:mm")}
                          </p>
                          {entry.oldValue && entry.newValue && (
                            <p style={{ margin: 0, fontSize: 12 }}>
                              {statusLabels[entry.oldValue] || entry.oldValue} →{" "}
                              {statusLabels[entry.newValue] || entry.newValue}
                            </p>
                          )}
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              </Panel>
            </Collapse>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PurchaseOrderShipmentDetail;
